import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  useHistory,
  ACTION_TYPES,
  ACTION_LABELS,
} from "../../hooks/useHistory";
import { useAuth } from "../../hooks/useAuth";
import { usePersonnel } from "../../hooks/usePersonnel";
import {
  FaFileExcel,
  FaFilter,
  FaHistory,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";
import "./History.css";

const normalizePersonalKey = (v) =>
  v === undefined || v === null ? "" : String(v).trim();

/** RETURNED from Signature/Refund (soldier) vs inventory-only זוכה */
const isEmployeeLinkedReturn = (activity) => {
  if (activity.actionType !== ACTION_TYPES.RETURNED) return false;
  const d = activity.details || {};
  return !!(
    (d.returnedFrom && String(d.returnedFrom).trim()) ||
    d.personalNumber
  );
};

const History = () => {
  const { user } = useAuth();
  const { personnel } = usePersonnel();
  const {
    history,
    loading,
    error,
    hasMore,
    loadingMore,
    fetchHistory,
    loadMoreHistory,
    getActionDescription,
  } = useHistory();

  const [selectedActionTypes, setSelectedActionTypes] = useState([]); // Array of selected action types
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showActionTypesChecklist, setShowActionTypesChecklist] =
    useState(false);
  const [collapsedDays, setCollapsedDays] = useState(new Set()); // Track collapsed day groups
  const checklistRef = useRef(null); // Ref for the checklist container
  const [appUsers, setAppUsers] = useState([]);
  const searchBackfillRunningRef = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const list = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            docId: docSnap.id,
            email: (data.email || docSnap.id || "").trim(),
            employeeId: data.employeeId,
          };
        });
        setAppUsers(list);
      },
      (err) => console.error("[History] users listener:", err),
    );
    return () => unsub();
  }, []);

  // Get user role
  const userPermissions = user?.permissions || "childUser";
  const userRole =
    userPermissions === "admin"
      ? "admin"
      : userPermissions === "parentsUser"
        ? "parentsUser"
        : "childUser";
  const userId = user?.email || user?.id || "";
  const isChildUser = userRole === "childUser";

  // For childUser: derive unit and unit personnel numbers for history scoping
  const childUserUnitId = useMemo(() => {
    if (!isChildUser) return null;
    const employeeId = user?.employeeId;
    if (!employeeId) return null;
    const currentUserPersonnel = personnel.find(
      (p) => p.personalNumber === employeeId,
    );
    return currentUserPersonnel?.unitId || null;
  }, [isChildUser, user?.employeeId, personnel]);

  const unitPersonnelNumbers = useMemo(() => {
    if (!isChildUser || !childUserUnitId || !personnel?.length) return null;
    return personnel
      .filter((p) => p.unitId === childUserUnitId)
      .map((p) => p.personalNumber);
  }, [isChildUser, childUserUnitId, personnel]);

  const personnelLookup = useMemo(() => {
    const byPersonalNumber = new Map();
    const byEmailLocal = new Map();
    const byEmailFull = new Map();
    if (!personnel || !Array.isArray(personnel)) {
      return { byPersonalNumber, byEmailLocal, byEmailFull };
    }

    personnel.forEach((person) => {
      const name = [person.firstName, person.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const pn = normalizePersonalKey(person.personalNumber);
      if (pn && name) {
        byPersonalNumber.set(pn, name);
      }
      if (person.email && person.email.includes("@") && name) {
        const em = person.email.trim().toLowerCase();
        byEmailFull.set(em, name);
        const local = person.email.split("@")[0].trim().toLowerCase();
        if (local) byEmailLocal.set(local, name);
      }
    });

    return { byPersonalNumber, byEmailLocal, byEmailFull };
  }, [personnel]);

  const userLookup = useMemo(() => {
    const byEmailLower = new Map();
    const byDocIdLower = new Map();
    appUsers.forEach((u) => {
      const emp = normalizePersonalKey(u.employeeId);
      if (!emp) return;
      const em = (u.email || "").trim().toLowerCase();
      if (em.includes("@")) byEmailLower.set(em, emp);
      const did = (u.docId || "").trim().toLowerCase();
      if (did) byDocIdLower.set(did, emp);
    });
    return { byEmailLower, byDocIdLower };
  }, [appUsers]);

  const getUserDisplayName = useCallback(
    (activity) => {
      const { byPersonalNumber, byEmailLocal, byEmailFull } = personnelLookup;
      const { byEmailLower, byDocIdLower } = userLookup;

      const nameFromEmployeeId = (empId) => {
        const k = normalizePersonalKey(empId);
        if (!k || byPersonalNumber.size === 0) return null;
        return byPersonalNumber.get(k) || null;
      };

      const fromEmp = nameFromEmployeeId(activity.userEmployeeId);
      if (fromEmp) return fromEmp;

      const emailCandidates = [
        activity.userEmail,
        typeof activity.userId === "string" && activity.userId.includes("@")
          ? activity.userId
          : null,
      ].filter(Boolean);

      for (const raw of emailCandidates) {
        const el = String(raw).trim().toLowerCase();
        if (!el) continue;
        const fromPersonnel = byEmailFull.get(el);
        if (fromPersonnel) return fromPersonnel;
        const empId =
          byEmailLower.get(el) || byDocIdLower.get(el);
        if (empId) {
          const n = nameFromEmployeeId(empId);
          if (n) return n;
        }
      }

      if (activity.userId && !String(activity.userId).includes("@")) {
        const empId = byDocIdLower.get(
          String(activity.userId).trim().toLowerCase(),
        );
        if (empId) {
          const n = nameFromEmployeeId(empId);
          if (n) return n;
        }
      }

      const localPart = (activity.userName || "").trim().toLowerCase();
      if (localPart && byEmailLocal.has(localPart)) {
        return byEmailLocal.get(localPart);
      }

      return activity.userName || "משתמש";
    },
    [personnelLookup, userLookup],
  );

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const initialAutoLoadRef = useRef(false);

  // Fetch history on mount and when filters change
  useEffect(() => {
    initialAutoLoadRef.current = false; // reset so auto-load re-runs after each fetch
    const loadHistory = async () => {
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      // Set end date to end of day
      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      await fetchHistory({
        userId,
        userRole,
        actionType: null,
        startDate: start,
        endDate: end,
        limitCount: 1500,
        unitPersonnelNumbers: isChildUser ? unitPersonnelNumbers : null,
      });
    };

    loadHistory();
  }, [
    userId,
    userRole,
    isChildUser,
    unitPersonnelNumbers,
    startDate,
    endDate,
    fetchHistory,
  ]);

  // Auto-load all remaining pages after initial fetch so all dates are visible
  useEffect(() => {
    if (loading) return;
    if (!hasMore) { initialAutoLoadRef.current = true; return; }
    if (initialAutoLoadRef.current) return;

    initialAutoLoadRef.current = true;
    let cancelled = false;

    (async () => {
      while (!cancelled) {
        const res = await loadMoreHistory();
        if (!res?.success || !res.hasMore) break;
      }
    })();

    return () => { cancelled = true; };
  }, [loading, hasMore, loadMoreHistory]);

  // Close checklist when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        checklistRef.current &&
        !checklistRef.current.contains(event.target) &&
        showActionTypesChecklist
      ) {
        setShowActionTypesChecklist(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionTypesChecklist]);

  // Optimized: Pre-compute searchable text for each activity (only when history or personnel changes)
  const activitiesWithSearchText = useMemo(() => {
    return history.map((activity) => {
      const searchableText = [
        activity.itemName || "",
        activity.serialNumber || "",
        getUserDisplayName(activity),
        activity.details?.assignedTo || "",
        activity.details?.returnedFrom || "",
      ]
        .join(" ")
        .toLowerCase();
      return { ...activity, searchableText };
    });
  }, [history, getUserDisplayName]);

  // Filter and group history by date (optimized)
  const groupedHistory = useMemo(() => {
    // Start with all history
    let filteredHistory = activitiesWithSearchText;

    // Filter by selected action types if any are selected
    if (selectedActionTypes.length > 0) {
      filteredHistory = filteredHistory.filter((activity) =>
        selectedActionTypes.some((sel) => {
          if (sel === ACTION_TYPES.RETURNED) {
            return (
              activity.actionType === ACTION_TYPES.RETURNED &&
              !isEmployeeLinkedReturn(activity)
            );
          }
          if (sel === ACTION_TYPES.MADE_AVAILABLE) {
            return (
              activity.actionType === ACTION_TYPES.MADE_AVAILABLE ||
              (activity.actionType === ACTION_TYPES.RETURNED &&
                isEmployeeLinkedReturn(activity))
            );
          }
          return activity.actionType === sel;
        }),
      );
    }

    // Filter by search term if provided (optimized - single includes check instead of multiple)
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filteredHistory = filteredHistory.filter((activity) =>
        activity.searchableText.includes(searchLower),
      );
    }

    const grouped = {};
    filteredHistory.forEach((activity) => {
      const date = new Date(activity.timestamp);
      const dateKey = date.toLocaleDateString("he-IL", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(activity);
    });

    // Sort dates (newest first)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      return (
        new Date(grouped[b][0].timestamp) - new Date(grouped[a][0].timestamp)
      );
    });

    return sortedDates.map((date) => ({
      date,
      activities: grouped[date].sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
      ),
    }));
  }, [activitiesWithSearchText, selectedActionTypes, debouncedSearchTerm]);

  // Toggle day collapse
  const toggleDayCollapse = useCallback((date) => {
    setCollapsedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  }, []);

  // Default view: newest day expanded, all older days collapsed.
  // When searching, expand all groups so all matching results are visible.
  useEffect(() => {
    if (!groupedHistory.length) {
      setCollapsedDays(new Set());
      return;
    }
    if (debouncedSearchTerm) {
      setCollapsedDays(new Set());
      return;
    }
    const nextCollapsed = new Set(
      groupedHistory.slice(1).map((group) => group.date),
    );
    setCollapsedDays(nextCollapsed);
  }, [groupedHistory, debouncedSearchTerm]);

  // Search should cover full history: while searching, keep paging in background.
  useEffect(() => {
    if (!debouncedSearchTerm.trim()) return;
    if (!hasMore || loading || loadingMore || searchBackfillRunningRef.current)
      return;

    searchBackfillRunningRef.current = true;
    let isCancelled = false;

    const backfillForSearch = async () => {
      try {
        // Load additional pages until exhausted, so search covers full available history.
        while (!isCancelled) {
          const res = await loadMoreHistory();
          if (!res?.success) break;
          if (!res.hasMore) break;
        }
      } finally {
        searchBackfillRunningRef.current = false;
      }
    };

    backfillForSearch();
    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchTerm, hasMore, loading, loadingMore, loadMoreHistory]);

  // Export to Excel
  const handleExportExcel = useCallback(() => {
    const exportData = history.map((activity) => {
      const date = new Date(activity.timestamp);
      return {
        תאריך: date.toLocaleDateString("he-IL"),
        שעה: date.toLocaleTimeString("he-IL"),
        משתמש: getUserDisplayName(activity),
        פעולה:
          activity.actionType === ACTION_TYPES.RETURNED
            ? isEmployeeLinkedReturn(activity)
              ? "הוחזר לזמין"
              : "זוכה"
            : ACTION_LABELS[activity.actionType] || activity.actionType,
        שם_פריט: activity.itemName,
        מספר_סידורי: activity.serialNumber || "",
        פרטים: getActionDescription(activity),
        תפקיד: activity.metadata?.userRole || "",
        יחידה: activity.metadata?.unitName || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "היסטוריה");

    // Set column widths
    const colWidths = [
      { wch: 12 }, // תאריך
      { wch: 10 }, // שעה
      { wch: 15 }, // משתמש
      { wch: 12 }, // פעולה
      { wch: 20 }, // שם_פריט
      { wch: 15 }, // מספר_סידורי
      { wch: 40 }, // פרטים
      { wch: 12 }, // תפקיד
      { wch: 15 }, // יחידה
    ];
    ws["!cols"] = colWidths;

    const fileName = `היסטוריה_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [history, getActionDescription, getUserDisplayName]);

  // Get action icon color
  const getActionColor = (actionType, activity = null) => {
    switch (actionType) {
      case ACTION_TYPES.ASSIGNED:
        return "#007bff";
      case ACTION_TYPES.RETURNED:
        if (activity && isEmployeeLinkedReturn(activity)) {
          return "#ffc107";
        }
        return "#fd7e14";
      case ACTION_TYPES.DELETED:
        return "#dc3545";
      case ACTION_TYPES.AMOUNT_CHANGED:
        return "#17a2b8";
      case ACTION_TYPES.SEEN:
        return "#28a745";
      case ACTION_TYPES.CREATED:
        return "#6c757d";
      case ACTION_TYPES.UPDATED:
        return "#6f42c1";
      case ACTION_TYPES.MADE_AVAILABLE:
        return "#ffc107"; // Same color as "הוחזר לזמין" (employee return)
      default:
        return "#6c757d";
    }
  };

  // Handle action type checkbox toggle
  const handleActionTypeToggle = (actionType) => {
    setSelectedActionTypes((prev) => {
      if (prev.includes(actionType)) {
        return prev.filter((type) => type !== actionType);
      } else {
        return [...prev, actionType];
      }
    });
  };

  // Select all / Deselect all action types
  const handleSelectAllActionTypes = () => {
    if (selectedActionTypes.length === Object.keys(ACTION_LABELS).length) {
      setSelectedActionTypes([]);
    } else {
      setSelectedActionTypes(Object.keys(ACTION_LABELS));
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedActionTypes([]);
    setStartDate("");
    setEndDate("");
    setSearchTerm("");
  };

  const handleExpandAllDays = useCallback(() => {
    setCollapsedDays(new Set());
  }, []);

  const handleCollapseAllDays = useCallback(() => {
    setCollapsedDays(new Set(groupedHistory.map((group) => group.date)));
  }, [groupedHistory]);

  if (loading && history.length === 0) {
    return (
      <div className="history-container">
        <div className="loading">טוען היסטוריה...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container">
        <div className="error">שגיאה בטעינת ההיסטוריה: {error}</div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <h1>
          <FaHistory /> היסטוריית פעילות
        </h1>
        <button
          className="export-btn"
          onClick={handleExportExcel}
          disabled={history.length === 0}
          title="ייצא לאקסל"
        >
          <FaFileExcel /> ייצא לאקסל
        </button>
      </div>

      {/* Filters */}
      <div className="history-filters">
        <div className="filter-group search-group">
          <label htmlFor="searchTerm">
            <FaSearch /> חיפוש:
          </label>
          <input
            id="searchTerm"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="חפש לפי שם פריט, צ', או שם משתמש..."
            className="filter-input search-input"
          />
        </div>

        <div className="filter-group action-types-group">
          <label>
            <FaFilter /> סוג פעולה:
          </label>
          <div className="action-types-checklist-container" ref={checklistRef}>
            <button
              type="button"
              className="action-types-toggle-btn"
              onClick={() =>
                setShowActionTypesChecklist(!showActionTypesChecklist)
              }
            >
              {selectedActionTypes.length === 0
                ? "כל הפעולות"
                : selectedActionTypes.length ===
                    Object.keys(ACTION_LABELS).length
                  ? "כל הפעולות נבחרו"
                  : `${selectedActionTypes.length} פעולות נבחרו`}
              <span className="toggle-arrow">
                {showActionTypesChecklist ? "▲" : "▼"}
              </span>
            </button>
            {showActionTypesChecklist && (
              <div className="action-types-checklist">
                <div className="checklist-header">
                  <button
                    type="button"
                    className="select-all-btn"
                    onClick={handleSelectAllActionTypes}
                  >
                    {selectedActionTypes.length ===
                    Object.keys(ACTION_LABELS).length
                      ? "בטל הכל"
                      : "בחר הכל"}
                  </button>
                </div>
                <div className="checklist-items">
                  {Object.entries(ACTION_LABELS).map(([key, label]) => (
                    <label key={key} className="checklist-item">
                      <input
                        type="checkbox"
                        checked={selectedActionTypes.includes(key)}
                        onChange={() => handleActionTypeToggle(key)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="date-range-group">
          <div className="filter-group">
            <label htmlFor="startDate">מתאריך:</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endDate">עד תאריך:</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>

        <button
          className="reset-filters-btn"
          onClick={handleResetFilters}
          title="אפס מסננים"
        >
          אפס מסננים
        </button>
      </div>

      {/* History Timeline with Collapsible Days */}
      <div className="history-collapse-controls">
        <button
          type="button"
          className="collapse-control-btn"
          onClick={handleExpandAllDays}
          disabled={groupedHistory.length === 0}
          title="הרחב את כל הימים"
        >
          הרחב הכל
        </button>
        <button
          type="button"
          className="collapse-control-btn"
          onClick={handleCollapseAllDays}
          disabled={groupedHistory.length === 0}
          title="צמצם את כל הימים"
        >
          צמצם הכל
        </button>
      </div>

      <div className="history-timeline-container">
        {groupedHistory.length === 0 ? (
          <div className="no-history">
            <p>אין פעילות להצגה</p>
            {(selectedActionTypes.length > 0 ||
              startDate ||
              endDate ||
              debouncedSearchTerm) && (
              <p className="filter-hint">נסה לשנות את המסננים או להסיר אותם</p>
            )}
          </div>
        ) : (
          <div className="history-timeline">
            {groupedHistory.map((item) => {
              const isCollapsed = collapsedDays.has(item.date);
              return (
                <div key={item.date} className="history-day-group">
                  <div
                    className="history-date-header clickable"
                    onClick={() => toggleDayCollapse(item.date)}
                    title={isCollapsed ? "הרחב" : "צמצם"}
                  >
                    <span>{item.date}</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span className="activity-count">
                        {item.activities.length} פעילויות
                      </span>
                      <span className="collapse-icon">
                        {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                      </span>
                    </div>
                  </div>
                  {!isCollapsed && (
                    <div className="history-activities">
                      {item.activities.map((activity) => {
                        const time = new Date(
                          activity.timestamp,
                        ).toLocaleTimeString("he-IL", {
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                        const actionColor = getActionColor(
                          activity.actionType,
                          activity,
                        );

                        return (
                          <div
                            key={activity.id}
                            className="history-activity"
                          >
                            <div
                              className="activity-indicator"
                              style={{ backgroundColor: actionColor }}
                            />
                            <div className="activity-content">
                              <div className="activity-header">
                                <span className="activity-time">
                                  {time}
                                </span>
                                <span
                                  className="activity-type"
                                  style={{ color: actionColor }}
                                >
                                  {activity.actionType ===
                                  ACTION_TYPES.RETURNED
                                    ? isEmployeeLinkedReturn(activity)
                                      ? "הוחזר לזמין"
                                      : "זוכה"
                                    : ACTION_LABELS[activity.actionType] ||
                                      activity.actionType}
                                </span>
                                <span className="activity-user">
                                  {getUserDisplayName(activity)}
                                </span>
                              </div>
                              <div className="activity-description">
                                {getActionDescription(activity)}
                              </div>
                              {activity.metadata?.unitName && (
                                <div className="activity-meta">
                                  יחידה: {activity.metadata.unitName}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Results count */}
      {groupedHistory.length > 0 && (
        <div className="history-footer">
          <p>
            נמצאו{" "}
            {groupedHistory.reduce(
              (sum, group) => sum + group.activities.length,
              0,
            )}{" "}
            פעילויות
            {debouncedSearchTerm && ` עבור "${debouncedSearchTerm}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default History;
